import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import { TaskService } from "../../../../services/task.service";
import { IVisitMapDetails } from "../../../../resources-vue/vue-interfaces/i-visit-map";
import { IAddressLongLat } from "../../../../resources-vue/vue-interfaces/i-address";
import { IName } from "../../../../resources-vue/vue-interfaces/i-name";
import { CalendarMapService } from "../../../../services-vue/calendar-map-service";
import { ToastrService } from "../../../../services/toastr.service";
import {
    formatFullAddress,
    formatScheduledTime,
    formatTimeInOut
} from "../../../../common/vue-helpers/modifiers/value-modifier";

import { ApplicationEnum } from "../../../../enums/ApplicationEnum";
import {
    getProductFullName,
    getProductPillClass
} from "../../../../common/vue-helpers/modifiers/application-modifiers";
import { EvvStatusHomeCareEnum } from "../../../../enums/EvvStatusHomeCareEnum";
import { EvvStatusHomeHealthEnum } from "../../../../enums/EvvStatusHomeHealthEnum";

import PatientVisitMap from "../../PatientCalendar/components/PatientVisitMap/PatientVisitMap.vue";
import FindNearbyEmployees from "../../PatientCalendar/components/FindNearbyEmployees/FindNearbyEmployees.vue";

export default defineComponent({
    components: {
        FindNearbyEmployees,
        PatientVisitMap
    },

    data(vm) {
        return {
            _taskService: null as TaskService,
            _toastrService: null as ToastrService,
            patientVisitDetails: null as IVisitMapDetails,
            _calendarMapService: null as CalendarMapService,
            _locationData: null as any,
            patientInfoWindow: "" as string,
            show: false,
            evvIconStatus: 0 as number,
            verificationVisitStatus: 0 as number
        };
    },
    async created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._calendarMapService = Container.instance.get(CalendarMapService);
    },
    async mounted() {
        this._locationData = JSON.parse(localStorage.getItem("axxess-user-location"));
        this.evvIconStatus = history.state.EvvIconStatus as number;
        this.verificationVisitStatus = history.state.VerificationVisitStatus as number;
        this.patientVisitDetails = this.buildPatientDetails();
        await this.getPatientVisitDetails();
    },
    methods: {
        async getPatientVisitDetails() {
            this.patientVisitDetails = this.buildPatientDetails();
            if (!this.patientVisitDetails.PatientAddress) {
                this._toastrService.error({
                    title: `Error`,
                    message: `Unable to Get Patient Visit Details`
                });
                this.closeUnassignedMap();
                return;
            }
            const coords = {
                lat: this.patientVisitDetails?.PatientAddress?.AddressLatitude,
                lng: this.patientVisitDetails?.PatientAddress?.AddressLongitude
            };
            if (!coords.lat && !coords.lng) {
                const address = formatFullAddress(this.patientVisitDetails.PatientAddress);
                if (address && address.length > 3) {
                    await this._calendarMapService.getCoordsFromAddress(address, (newcoords) => {
                        this.patientVisitDetails.PatientAddress.AddressLatitude = Number(newcoords.lat);
                        this.patientVisitDetails.PatientAddress.AddressLongitude = Number(newcoords.lng);
                        this.show = true;
                    });
                }
            } else {
                await this._calendarMapService.loadMapsApi();
                this.show = true;
            }

            this.patientInfoWindow = this.getPatientInfoWindowContent([this.patientVisitDetails]);
        },

        getPatientInfoWindowContent(visits: any): string {
            let patientVisits: string = "";
            const patientName = visits[0].PatientName;
            let tempApplicationId: ApplicationEnum;
            if (visits[0].Application === 256) {
                tempApplicationId = ApplicationEnum.AxxessHospice;
            } else if (visits[0].Application === 65536) {
                tempApplicationId = ApplicationEnum.AxxessPalliative;
            } else {
                tempApplicationId = visits[0].Application;
            }

            for (const v of visits) {
                patientVisits += this.getVisitDetails(v);
            }

            return ` <div id="${visits[0].Id}" class="visit-marker-details">
                        <span class="${getProductPillClass(tempApplicationId)} pill">
                            ${getProductFullName(tempApplicationId)}
                        </span>
                        <big class="info-header"><b>${patientName.FirstName} ${patientName.LastName}</b></big>
                        ${patientVisits}
                        <div class="visit-address">${formatFullAddress(visits[0].PatientAddress)}</div>
                        </div>`;
        },
        getVisitDetails(visit: any) {
            const visitTime = formatTimeInOut(
                formatScheduledTime(visit.StartDateTime),
                formatScheduledTime(visit.EndDateTime)
            );

            visit.EvvIconStatus = this.evvIconStatus;
            visit.VerificationVisitStatus = this.verificationVisitStatus;

            let evvHouse = null;
            if (visit.Application == ApplicationEnum.HomeCare) {
                if (
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.Finished ||
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                ) {
                    evvHouse =
                        visit.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                            ? `<a class='evv'><i class='evv-adjusted signed'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgress ||
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                ) {
                    evvHouse =
                        visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                            ? `<a class='evv'><i class='evv-adjusted in-progress'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md'></i></a>`;
                } else if (visit.EvvIconStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            } else if (visit.Application == ApplicationEnum.AgencyCore) {
                if (visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.InProgress) {
                    evvHouse = `<a class='evv ps-1'><i class='fa fa-home fa-md'></i></a>`;
                } else if (
                    visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.PatientVerified ||
                    visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.ClinicianVerified
                ) {
                    evvHouse = `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            }
            return `<div class="visit-details pe-5">
                                <div class="font-weight-normal">${visit.VisitName}</div>
                                <div>${visit.Status}</div>
                                <div>${visitTime}</div>
                                <div class="evvHouse">${evvHouse ? evvHouse : ""}</div>
                    </div>`;
        },

        closeUnassignedMap() {
            this.$router.back();
        },

        buildPatientDetails(): IVisitMapDetails {
            return {
                VisitName: history.state.taskName,
                PatientName: {
                    FirstName: history.state.FirstName,
                    LastName: history.state.LastName
                } as IName,
                PatientAddress: JSON.parse(history.state.PatientAddress) as IAddressLongLat,
                PatientId: history.state.PatientId,
                LocationId: history.state.BranchId,
                CustomTaskId: history.state.CustomTaskId,
                Application: history.state.Application,
                AgencyId: history.state.agencyId,
                Status: history.state.Status,
                StartDateTime: history.state.StartDateTime,
                EndDateTime: history.state.EndDateTime,
                Id: history.state.TaskId,
                VisitDate: moment(history.state.VisitDate).format("MM/DD/YYYY"),
                EmployeeName: {
                    FirstName: null,
                    LastName: null
                } as IName
            } as IVisitMapDetails;
        }
    }
});
